import { Link, Outlet } from 'react-router-dom';
import { useCustomization } from '../../contexts/CustomizationContext';

type Props = {
  module: string;
  /** Optional inline children — if omitted, renders <Outlet /> for nested routes. */
  children?: React.ReactNode;
};

/**
 * Hides a feature module when the SuperAdmin has toggled it off.
 *
 * Two usage shapes:
 *   1) <Route element={<ModuleGuard module="training" />}> ...children... </Route>
 *      Renders the nested <Outlet /> when enabled.
 *   2) <ModuleGuard module="community">{content}</ModuleGuard>
 *      Inline wrapper around a single node.
 *
 * When disabled, renders a small "feature unavailable" panel instead of
 * the route content — better UX than a hard redirect because the user
 * sees *why* the page is gone.
 */
export const ModuleGuard = ({ module, children }: Props) => {
  const { isModuleEnabled, loading } = useCustomization();
  if (loading) return null; // brief flash; the rest of the app suppresses spinners
  if (isModuleEnabled(module)) {
    return children ? <>{children}</> : <Outlet />;
  }
  return (
    <main className="mx-auto max-w-2xl px-6 py-16 text-center">
      <p className="text-xs font-black uppercase tracking-wider text-[#a44c00]">Feature unavailable</p>
      <h1 className="mt-2 text-3xl font-black text-[#000080]">This area is disabled on your platform.</h1>
      <p className="mt-3 text-sm text-slate-600">
        The platform administrator has turned off the <strong>{module}</strong> module. Reach out to
        your platform admin if you believe this is a mistake.
      </p>
      <Link to="/" className="mt-6 inline-block rounded-xl bg-[#FF9933] px-5 py-2.5 text-sm font-black text-[#000080]">
        Back to home
      </Link>
    </main>
  );
};
